import Api from '../api';
import type { TravelRuleAddressListResponse } from '@/types/travel-rule-address';
import type { SaveConsentPayload } from '@/types/consent';

const PRODUCT_TYPE_WEBSITE = 'website';

export default {
    validateCryptoAddress({ language, ...jsonData }) {
        return this._post('v1/webwallet/validate_crypto_address', jsonData, {
            headers: {
                'X-User-Locale': language,
            },
        });
    },

    getNotifications(params = {}) {
        return this._get('v1/users/notifications', {
            ...params,
            businessAccountProductId: BUSINESS_ACCOUNT_PRODUCT_ID, // eslint-disable-line no-undef
        });
    },

    getNotificationsUnreadCount(params = {}) {
        return this._get('v1/users/notifications/unread-count', {
            ...params,
            businessAccountProductId: BUSINESS_ACCOUNT_PRODUCT_ID, // eslint-disable-line no-undef
        });
    },

    markNotificationsAsReadByIds(ids = []) {
        return this._post('v1/users/notifications/read', {
            notificationIds: ids,
            businessAccountProductId: BUSINESS_ACCOUNT_PRODUCT_ID, // eslint-disable-line no-undef
        });
    },

    getTransactions(TransactionFilterBuilder, { page, pageSize }) {
        return this._get('v1/user-transaction', {
            ...TransactionFilterBuilder.getFormatted(),
            page,
            pageSize,
            productType: PRODUCT_TYPE_WEBSITE,
        });
    },

    getTransactionsByLimit(limit) {
        return this._get('v1/user-transaction', {
            pageSize: limit,
            productType: PRODUCT_TYPE_WEBSITE,
        });
    },

    getUser() {
        return this._get('v1/users/me');
    },

    getTravelRuleAddresses(
        blockchain: string,
        network: string | null = null,
    ): Promise<TravelRuleAddressListResponse> {
        const blockchainParam = network ? `${blockchain}_${network}` : blockchain;

        return this._get('v1/travel-rule-address', { blockchain: blockchainParam });
    },

    // Exact lookup for a single saved beneficiary: the same address can have several saved
    // entries with different destination tags, so the response may still contain more than one
    // item — the caller filters by the exact (address, tag) pair. The tag is only sent when
    // present; a tag-less field omits `destinationTag` entirely.
    findTravelRuleAddress(
        blockchain: string,
        network: string | null,
        cryptoAddress: string,
        destinationTag: string | null = null,
    ): Promise<TravelRuleAddressListResponse> {
        const blockchainParam = network ? `${blockchain}_${network}` : blockchain;
        const tag = destinationTag?.trim();
        const params: { blockchain: string; cryptoAddress: string; destinationTag?: string } = {
            blockchain: blockchainParam,
            cryptoAddress,
        };
        if (tag) {
            params.destinationTag = tag;
        }

        return this._get('v1/travel-rule-address', params);
    },

    acceptTermsOfService() {
        return this._post('v1/user/terms', { isTosAccepted: true });
    },

    saveLeadConsents(consents: SaveConsentPayload[], emailProof: string) {
        return this._post('v1/consents/lead', { consents, emailProof });
    },

    saveUserConsents(consents: SaveConsentPayload[]) {
        return this._post('v1/consents/user', { consents });
    },


    saveUserDecisionAfterInvitationPopUp(decision) {
        return this._patch('v1/users/stay-in-app-terms', {
            stayInApp: decision,
        });
    },

    getExchangeLinksByAssets(assetIds) {
        return this._get('v1/wallet/asset/options', {
            assetIds,
        }).then(({ data }) => data);
    },

    _addTransactionData(version, context, invoice, data) {
        return this._post(`${version}/transaction/${invoice}/data/${context}`, data);
    },

    addTransactionBeneficiaryData(invoice, data) {
        return this._addTransactionData('v1', 'beneficiary', invoice, data);
    },

    addTransactionBeneficiaryDataV2(invoice, data) {
        return this._addTransactionData('v2', 'beneficiary', invoice, data);
    },

    addTransactionOriginatorData(invoice, data) {
        return this._addTransactionData('v1', 'originator', invoice, data);
    },

    addTransactionOriginatorDataV2(invoice, data) {
        return this._addTransactionData('v2', 'originator', invoice, data);
    },

    _patch(path, jsonData) {
        return Api()
          .patch(this._getApiUrl(path), jsonData);
    },

    _get(path, params) {
        return Api()
            .get(this._getApiUrl(path), { params })
            .then(({ data }) => data);
    },

    _post(path, jsonData, params) {
        return Api()
            .post(this._getApiUrl(path), jsonData, params);
    },

    _getApiUrl(path) {
        const rootUrl = PROCESSING_API_URL; // eslint-disable-line no-undef

        return `${rootUrl}/public/processing/${path}`;
    },
};
